import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { useT } from "@/i18n";

const WHATSAPP_LINK = "https://wa.me/819045660098?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20o%20plano%20";

const containerVariants = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const itemVariants = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function PlansSection() {
  const t = useT();
  const p = t.plans;

  const plans = [
    {
      name: p.basic, data: "3GB", calls: p.unlimitedCalls, price: "3,480", highlight: false, badge: null,
      features: [`3GB ${p.features.data}`, p.features.callsUnlimited, p.features.jpNumber, p.features.simOrEsim, p.features.supportPt],
      optional: null,
    },
    {
      name: p.popular, data: "20GB", calls: `60 min ${p.calls60.replace("60 min ", "")}`, price: "3,980", highlight: true, badge: p.badge,
      features: [`20GB ${p.features.data}`, p.features.calls60min, p.features.jpNumber, p.features.simOrEsim, p.features.supportPt],
      optional: `${p.unlimitedPlus}: +¥1,500`,
    },
    {
      name: p.premium, data: "50GB", calls: `60 min ${p.calls60.replace("60 min ", "")}`, price: "4,980", highlight: false, badge: null,
      features: [`50GB ${p.features.data}`, p.features.calls60min, p.features.jpNumber, p.features.simOrEsim, p.features.supportPriority],
      optional: `${p.unlimitedPlus}: +¥1,000`,
    },
  ];

  return (
    <section id="planos" data-testid="plans-section" className="py-24 sm:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-center mb-16">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-orange-400 mb-4">{p.overline}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-4">{p.title}</h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-xl mx-auto">{p.sub}</p>
        </motion.div>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-start">
          {plans.map((plan) => (
            <motion.div key={plan.name} variants={itemVariants} data-testid={`plan-card-${plan.data.toLowerCase()}`}
              className={`relative flex flex-col p-8 rounded-3xl transition-all duration-300 ${plan.highlight ? "bg-slate-900 border border-orange-500/50 shadow-[0_0_30px_-10px_rgba(249,115,22,0.3)] md:-translate-y-4" : "bg-slate-900/50 border border-white/10 backdrop-blur-sm hover:border-white/20"}`}>
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-red-600 to-orange-500 text-white">
                    <Star className="w-3 h-3" fill="currentColor" strokeWidth={1.5} />{plan.badge}
                  </span>
                </div>
              )}
              <div className="mb-6 pt-2">
                <h3 className="text-lg font-medium text-slate-400 mb-1">{plan.name}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-sm text-slate-500">¥</span>
                  <span className="text-5xl font-semibold tracking-tighter text-white">{plan.price}</span>
                  <span className="text-sm text-slate-500">/{p.month}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-orange-500/10 text-orange-300 text-sm font-medium">
                  {plan.data} + {plan.calls}
                </span>
              </div>
              {plan.optional && <p className="text-xs text-slate-500 mb-4 pl-1">{p.optional}: {plan.optional}</p>}
              <div className="mb-4 px-3 py-2 rounded-lg" style={{ background: "rgba(249,115,22,0.06)", border: "1px solid rgba(249,115,22,0.12)" }}>
                <p className="text-sm font-semibold text-orange-400">{p.activationFee}</p>
              </div>
              <div className="flex-1 my-4 space-y-3">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-orange-400 flex-shrink-0" strokeWidth={2} />
                    <span className="text-sm text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>
              <a href={`${WHATSAPP_LINK}${plan.name}%20(${plan.data})`} target="_blank" rel="noopener noreferrer" data-testid={`plan-cta-${plan.data.toLowerCase()}`}
                className={`w-full py-4 rounded-xl font-medium text-center transition-all duration-300 block ${plan.highlight ? "btn-gradient text-white" : "bg-white/5 text-white border border-white/10 hover:bg-white/10"}`}>
                {p.cta}
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
